import { createFeature, createReducer, on } from '@ngrx/store';

import {
  changeCropFieldFilter,
  changeCropFieldsPage,
  changeCustomerFilter,
  changeSearchFilter,
  changeSelectedCropYear,
  changeStatusFilter,
  confirmCancellationsFailure,
  confirmCancellationsSuccess,
  loadBBACropFieldsFailure,
  loadBBACropFieldsRequest,
  loadBBACropFieldsSuccess,
  resetAllFilters,
  toggleCancellationFailure,
  toggleCancellationSuccess,
  toggleIsAdministrationInformedOfCancellationFailure,
  toggleIsAdministrationInformedOfCancellationSuccess,
  toggleIsFertilabInformedOfCancellationFailure,
  toggleIsFertilabInformedOfCancellationSuccess,
} from './bba-cancellation.actions';
import { BbaCancellationStatusFilter, BbaCropField } from '../models/bba-cancellation.model';

export interface PaginatedResource<TData, TFilter> {
  data: TData[];
  isLoading: boolean;
  isError: boolean;
  filter: TFilter;
  pagination: {
    pageNumber: number;
    pageSize: number;
    totalItemCount: number;
  };
}

export interface BbaCropFieldFilter {
  cropYear: number;
  statusFilter: BbaCancellationStatusFilter;
  searchTerm: string;
  customerName: string;
  cropFieldName: string;
}

export interface BBACancellationState {
  cropFields: PaginatedResource<BbaCropField, BbaCropFieldFilter>;
}

const defaultPageNumber = 0;

const initialFilter: BbaCropFieldFilter = {
  cropYear: new Date().getFullYear(),
  statusFilter: 'All',
  searchTerm: '',
  customerName: '',
  cropFieldName: '',
};

const initialState: BBACancellationState = {
  cropFields: {
    data: [],
    isLoading: false,
    isError: false,
    filter: initialFilter,
    pagination: {
      pageNumber: defaultPageNumber,
      pageSize: 10,
      totalItemCount: 0,
    },
  },
};

export const bbaCancellationFeature = createFeature({
  name: 'bbaCancellation',
  reducer: createReducer(
    initialState,
    on(
      loadBBACropFieldsRequest,
      (state): BBACancellationState => ({
        ...state,
        cropFields: {
          ...state.cropFields,
          isLoading: true,
          isError: false,
        },
      }),
    ),
    on(
      loadBBACropFieldsSuccess,
      (state, { cropFields, totalCropFieldsCount }): BBACancellationState => ({
        ...state,
        cropFields: {
          ...state.cropFields,
          data: cropFields,
          isLoading: false,
          isError: false,
          pagination: {
            ...state.cropFields.pagination,
            totalItemCount: totalCropFieldsCount,
          },
        },
      }),
    ),
    on(
      loadBBACropFieldsFailure,
      (state): BBACancellationState => ({
        ...state,
        cropFields: {
          ...state.cropFields,
          isLoading: false,
          isError: true,
        },
      }),
    ),
    on(
      changeSelectedCropYear,
      (state, { cropYear }): BBACancellationState => ({
        ...state,
        cropFields: {
          ...state.cropFields,
          filter: {
            ...state.cropFields.filter,
            cropYear,
          },
          pagination: {
            ...state.cropFields.pagination,
            pageNumber: defaultPageNumber,
          },
        },
      }),
    ),
    on(
      changeStatusFilter,
      (state, { statusFilter }): BBACancellationState => ({
        ...state,
        cropFields: {
          ...state.cropFields,
          filter: {
            ...state.cropFields.filter,
            statusFilter,
          },
          pagination: {
            ...state.cropFields.pagination,
            pageNumber: defaultPageNumber,
          },
        },
      }),
    ),
    on(
      changeSearchFilter,
      (state, { searchTerm }): BBACancellationState => ({
        ...state,
        cropFields: {
          ...state.cropFields,
          filter: {
            ...state.cropFields.filter,
            searchTerm,
          },
          pagination: {
            ...state.cropFields.pagination,
            pageNumber: defaultPageNumber,
          },
        },
      }),
    ),
    on(
      changeCustomerFilter,
      (state, { searchTerm }): BBACancellationState => ({
        ...state,
        cropFields: {
          ...state.cropFields,
          filter: {
            ...state.cropFields.filter,
            customerName: searchTerm,
          },
          pagination: {
            ...state.cropFields.pagination,
            pageNumber: defaultPageNumber,
          },
        },
      }),
    ),
    on(
      changeCropFieldFilter,
      (state, { searchTerm }): BBACancellationState => ({
        ...state,
        cropFields: {
          ...state.cropFields,
          filter: {
            ...state.cropFields.filter,
            cropFieldName: searchTerm,
          },
          pagination: {
            ...state.cropFields.pagination,
            pageNumber: defaultPageNumber,
          },
        },
      }),
    ),
    on(
      resetAllFilters,
      (state): BBACancellationState => ({
        ...state,
        cropFields: {
          ...state.cropFields,
          filter: initialFilter,
          pagination: {
            ...state.cropFields.pagination,
            pageNumber: defaultPageNumber,
          },
        },
      }),
    ),
    on(
      changeCropFieldsPage,
      (state, { pageNumber, pageSize }): BBACancellationState => ({
        ...state,
        cropFields: {
          ...state.cropFields,
          pagination: {
            ...state.cropFields.pagination,
            pageNumber,
            pageSize,
          },
        },
      }),
    ),
    on(
      toggleCancellationSuccess,
      (state, { adviceId, setCancelled }): BBACancellationState => ({
        ...state,
        cropFields: {
          ...state.cropFields,
          data: state.cropFields.data.map((cropField) => (cropField.adviceId === adviceId ? { ...cropField, isCancelled: setCancelled } : cropField)),
        },
      }),
    ),
    on(
      toggleCancellationFailure,
      (state, { adviceId, setCancelled }): BBACancellationState => ({
        ...state,
        cropFields: {
          ...state.cropFields,
          data: state.cropFields.data.map((cropField) => (cropField.adviceId === adviceId ? { ...cropField, isCancelled: setCancelled } : cropField)),
        },
      }),
    ),
    on(
      toggleIsFertilabInformedOfCancellationSuccess,
      (state, { isFertilabInformed, adviceId }): BBACancellationState => ({
        ...state,
        cropFields: {
          ...state.cropFields,
          data: state.cropFields.data.map((cropField) =>
            cropField.adviceId === adviceId ? { ...cropField, isFertilabInformedOfCancellation: isFertilabInformed } : cropField,
          ),
        },
      }),
    ),
    on(
      toggleIsFertilabInformedOfCancellationFailure,
      (state, { isFertilabInformed, adviceId }): BBACancellationState => ({
        ...state,
        cropFields: {
          ...state.cropFields,
          data: state.cropFields.data.map((cropField) =>
            cropField.adviceId === adviceId ? { ...cropField, isFertilabInformedOfCancellation: isFertilabInformed } : cropField,
          ),
        },
      }),
    ),
    on(
      toggleIsAdministrationInformedOfCancellationSuccess,
      (state, { isAdministrationInformed, adviceId }): BBACancellationState => ({
        ...state,
        cropFields: {
          ...state.cropFields,
          data: state.cropFields.data.map((cropField) =>
            cropField.adviceId === adviceId ? { ...cropField, isAdministrationInformedOfCancellation: isAdministrationInformed } : cropField,
          ),
        },
      }),
    ),
    on(
      toggleIsAdministrationInformedOfCancellationFailure,
      (state, { isAdministrationInformed, adviceId }): BBACancellationState => ({
        ...state,
        cropFields: {
          ...state.cropFields,
          data: state.cropFields.data.map((cropField) =>
            cropField.adviceId === adviceId ? { ...cropField, isAdministrationInformedOfCancellation: isAdministrationInformed } : cropField,
          ),
        },
      }),
    ),

    on(
      confirmCancellationsSuccess,
      (state): BBACancellationState => ({
        ...state,
      }),
    ),
    on(
      confirmCancellationsFailure,
      (state): BBACancellationState => ({
        ...state,
      }),
    ),
  ),
});

export const { selectCropFields: selectCropFieldsState } = bbaCancellationFeature;
